import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Button from '../components/Button';
import AdminPage from '../components/AdminShell';

export default function AdminFlagsPage() {
  const { API_URL } = useAuth();
  const MESSAGING_URL = `${API_URL.replace('/api', '')}/messaging`;

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  // Flagged messages state
  const [flags, setFlags] = useState([]);
  const [flagsLoading, setFlagsLoading] = useState(true);
  const [expandedFlagId, setExpandedFlagId] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [loadingConversation, setLoadingConversation] = useState(false);

  // Coach review state
  const [coachPending, setCoachPending] = useState([]);
  const [coachLoading, setCoachLoading] = useState(true);
  const [coachMessage, setCoachMessage] = useState('');

  const loadFlags = useCallback(async () => {
    try {
      setFlagsLoading(true);
      const response = await axios.get(`${MESSAGING_URL}/flags/pending`, getAxiosConfig());
      setFlags(response.data.flags || []);
    } catch (error) {
      console.error('Error loading flags:', error);
    } finally {
      setFlagsLoading(false);
    }
  }, [MESSAGING_URL]);

  const loadCoachPending = useCallback(async () => {
    try {
      setCoachLoading(true);
      const response = await axios.get(`${API_URL}/admin/coach-review/pending`, getAxiosConfig());
      setCoachPending(response.data || []);
    } catch (error) {
      console.error('Error loading coach reviews:', error);
    } finally {
      setCoachLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    loadFlags();
    loadCoachPending();
  }, [loadFlags, loadCoachPending]);

  const handleFlagAction = async (flagId, action) => {
    try {
      await axios.put(`${MESSAGING_URL}/flags/${flagId}`, { action }, getAxiosConfig());
      setFlags(prev => prev.filter(f => f.id !== flagId));
      if (expandedFlagId === flagId) {
        setExpandedFlagId(null);
        setConversationMessages([]);
      }
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to ${action} flag`);
    }
  };

  const handleCoachAction = async (userId, action, name) => {
    if (action === 'deny' && !window.confirm(`Deny coach application for ${name}? They will be notified by email.`)) return;
    try {
      await axios.post(`${API_URL}/admin/coach-review/${userId}/${action}`, {}, getAxiosConfig());
      setCoachMessage(`${name} ${action === 'approve' ? 'approved as coach' : 'denied — role set to Base User'}`);
      setTimeout(() => setCoachMessage(''), 4000);
      loadCoachPending();
    } catch (error) {
      setCoachMessage('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const toggleConversation = async (flagId) => {
    if (expandedFlagId === flagId) {
      setExpandedFlagId(null);
      setConversationMessages([]);
      return;
    }
    setExpandedFlagId(flagId);
    setLoadingConversation(true);
    try {
      const response = await axios.get(`${MESSAGING_URL}/flags/${flagId}/conversation`, getAxiosConfig());
      setConversationMessages(response.data.messages || []);
    } catch (error) {
      setConversationMessages([]);
    } finally {
      setLoadingConversation(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const renderConversation = () => {
    if (loadingConversation) {
      return <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>Loading conversation...</div>;
    }
    if (conversationMessages.length === 0) {
      return <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>No messages found.</div>;
    }
    let lastSenderId = null;
    return (
      <div style={{
        background: '#f1f5f9', borderRadius: '8px', padding: '16px',
        maxHeight: '500px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px',
      }}>
        {conversationMessages.map((msg) => {
          const showName = msg.sender_id !== lastSenderId;
          lastSenderId = msg.sender_id;
          const isFlagged = msg.is_flagged;
          return (
            <div key={msg.id} style={{ marginTop: showName ? '12px' : '2px' }}>
              {showName && (
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '2px' }}>
                  {msg.sender_name}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                <div style={{
                  padding: '8px 12px', borderRadius: '8px',
                  background: isFlagged ? '#fef2f2' : '#ffffff',
                  border: isFlagged ? '2px solid #f87171' : '1px solid #e2e8f0',
                  maxWidth: '80%', position: 'relative',
                }}>
                  {isFlagged && (
                    <span style={{
                      position: 'absolute', top: '-8px', right: '-8px',
                      background: '#dc2626', color: '#fff', fontSize: '10px', fontWeight: 700,
                      borderRadius: '4px', padding: '1px 5px',
                    }}>FLAGGED</span>
                  )}
                  {msg.content && <div style={{ fontSize: '14px', color: '#1e293b' }}>{msg.content}</div>}
                  {msg.attachment_url && (
                    <div style={{ marginTop: msg.content ? '4px' : '0' }}>
                      {msg.attachment_type?.startsWith('image') ? (
                        <img src={msg.attachment_url} alt="attachment"
                          style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '4px' }} />
                      ) : (
                        <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: '13px', color: '#4D8064' }}>View attachment</a>
                      )}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {formatTime(msg.created_at)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const totalPending = (coachLoading ? 0 : coachPending.length) + (flagsLoading ? 0 : flags.length);

  return (
    <AdminPage
      active="flags"
      title="Admin Notifications"
      subtitle={totalPending > 0 ? `${totalPending} item${totalPending !== 1 ? 's' : ''} require attention` : 'All clear'}
    >

      {/* ── Coach Applications ─────────────────────────────── */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          Coach Applications
          {!coachLoading && coachPending.length > 0 && (
            <span style={{ background: '#3b82f6', color: '#fff', fontSize: '11px', fontWeight: 700, borderRadius: '10px', padding: '1px 8px' }}>
              {coachPending.length}
            </span>
          )}
        </h2>

        {coachMessage && (
          <div className={coachMessage.includes('Error') ? 'settings-toast settings-toast--error' : 'settings-toast'}
            style={{ position: 'relative', top: 'auto', right: 'auto', marginBottom: '12px' }}>
            {coachMessage}
          </div>
        )}

        {coachLoading ? (
          <div style={{ color: '#64748b', padding: '16px 0' }}>Loading...</div>
        ) : coachPending.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
            No pending coach applications.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {coachPending.map(coach => (
              <div key={coach.id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b' }}>
                      {coach.first_name} {coach.last_name}
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>{coach.email}</div>
                    {coach.school && (
                      <div style={{ fontSize: '13px', color: '#475569', marginTop: '2px' }}>School: <strong>{coach.school}</strong></div>
                    )}
                    {(coach.city || coach.state) && (
                      <div style={{ fontSize: '13px', color: '#475569', marginTop: '2px' }}>
                        Location: <strong>{[coach.city, coach.state].filter(Boolean).join(', ')}</strong>
                      </div>
                    )}
                    {coach.created_at && (
                      <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>Applied {formatTime(coach.created_at)}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <Button
                      variant="accent"
                      size="sm"
                      onClick={() => handleCoachAction(coach.id, 'approve', `${coach.first_name} ${coach.last_name}`)}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleCoachAction(coach.id, 'deny', `${coach.first_name} ${coach.last_name}`)}
                    >
                      Deny
                    </Button>
                  </div>
                </div>
                {coach.sex_offender_matches && coach.sex_offender_matches.length > 0 && (
                  <div style={{
                    marginTop: '12px', background: '#fff7ed', border: '2px solid #f97316',
                    borderRadius: '6px', padding: '12px',
                  }}>
                    <div style={{ fontWeight: 700, color: '#c2410c', fontSize: '13px', marginBottom: '6px' }}>
                      ⚠ Name match found on sex offender registry — verify before approving
                    </div>
                    {coach.sex_offender_matches.map((match, i) => (
                      <div key={i} style={{ fontSize: '13px', color: '#7c2d12', marginTop: '4px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600 }}>{match.state}</span>
                        <span style={{ color: '#92400e' }}>Risk: {match.risk_level}</span>
                        {match.registry_url && (
                          <a href={match.registry_url} target="_blank" rel="noopener noreferrer"
                            style={{ color: '#c2410c', fontSize: '12px' }}>
                            View Registry &rarr;
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Flagged Messages ───────────────────────────────── */}
      <div>
        <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          Flagged Messages
          {!flagsLoading && flags.length > 0 && (
            <span style={{ background: '#dc2626', color: '#fff', fontSize: '11px', fontWeight: 700, borderRadius: '10px', padding: '1px 8px' }}>
              {flags.length}
            </span>
          )}
        </h2>

        {flagsLoading ? (
          <div style={{ color: '#64748b', padding: '16px 0' }}>Loading...</div>
        ) : flags.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
            No pending flagged messages.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {flags.map(flag => (
              <div key={flag.id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>
                      Flagged by <strong>{flag.flagged_by?.name}</strong> &middot; {formatTime(flag.created_at)}
                    </span>
                    {flag.conversation_participants && (
                      <div style={{ fontSize: '13px', color: '#475569', marginTop: '2px' }}>
                        Conversation between: <strong>{flag.conversation_participants.join(' & ')}</strong>
                      </div>
                    )}
                    {flag.reason && (
                      <div style={{ fontSize: '13px', color: '#92400e', background: '#fef3c7', borderRadius: '4px', padding: '4px 8px', marginTop: '4px', display: 'inline-block' }}>
                        Reason: {flag.reason}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <Button variant="primary" size="sm" onClick={() => handleFlagAction(flag.id, 'resolve')}>Resolve</Button>
                    <Button variant="secondary" size="sm" onClick={() => handleFlagAction(flag.id, 'dismiss')}>Dismiss</Button>
                  </div>
                </div>

                <div style={{
                  padding: '8px 12px', background: '#fef2f2', borderRadius: '6px',
                  border: '1px solid #fecaca', marginBottom: '12px',
                }}>
                  <span style={{ fontWeight: 600, color: '#dc2626' }}>{flag.message_sender?.name}:</span>{' '}
                  <span style={{ color: '#1a202c' }}>{flag.message?.content}</span>
                  <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '8px' }}>{formatTime(flag.message?.created_at)}</span>
                </div>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => toggleConversation(flag.id)}
                  style={{ marginBottom: expandedFlagId === flag.id ? '12px' : '0' }}
                >
                  {expandedFlagId === flag.id ? 'Hide Full Conversation' : 'View Full Conversation'}
                </Button>

                {expandedFlagId === flag.id && renderConversation()}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminPage>
  );
}
